import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  error?: string | { message?: string } | undefined;
  label?: string;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    error, 
    label, 
    size = 'medium', 
    variant = 'outlined', 
    fullWidth = true, 
    ...props 
  }, ref) => {
    const sizeClass = size === 'small' ? 'h-9 text-sm' : 'h-10 text-sm';
    const errorMessage = typeof error === 'string'
      ? error
      : error && typeof error === 'object' && 'message' in error
        ? String(error.message || '')
        : undefined;

    const variantClass =
      variant === 'filled'
        ? 'border border-transparent bg-slate-100/95 shadow-[inset_0_1px_1px_rgba(15,23,42,0.05)] dark:bg-slate-800/80'
        : variant === 'standard'
          ? 'rounded-none border-0 border-b border-slate-300 dark:border-slate-600 bg-transparent px-0 shadow-none'
          : 'border border-slate-200/90 dark:border-slate-700 bg-white/90 dark:bg-slate-900/72';

    return (
      <div className={cn(fullWidth ? 'w-full' : 'inline-flex', 'flex-col gap-1')}>
        {label && (
          <label className="text-sm font-medium text-foreground">{label}</label>
        )}
        <input
          ref={ref}
          className={cn(
            "w-full rounded-xl px-3.5 py-2.5 text-foreground ring-offset-background shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 placeholder:text-muted-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:border-blue-300",
            sizeClass,
            variantClass,
            error && 'border-red-500 focus-visible:ring-red-500/70',
            className
          )}
          {...props}
        />
        {errorMessage && <p className="text-sm text-red-500">{errorMessage}</p>}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };